// Local Business Outreach Data — 30-mile radius of Yardley, PA (19067)
// Add businesses you find via Google Maps, Yelp, or local search.
// Rate website (webScore) and social (socialScore) on 1–5: 1 = terrible / none, 5 = excellent.
// Opportunity score is auto-calculated — lower quality presence = higher priority.

// ─── Business Types ────────────────────────────────────────────────────────────

export const BUSINESS_TYPES = {
  restaurant:  { label: 'Restaurant / Bar',          emoji: '🍽️' },
  brewery:     { label: 'Brewery / Taproom',          emoji: '🍺' },
  retail:      { label: 'Retail / Boutique',          emoji: '🛍️' },
  fitness:     { label: 'Fitness / Gym / Studio',     emoji: '💪' },
  realestate:  { label: 'Real Estate',                emoji: '🏡' },
  venue:       { label: 'Event Venue / Wedding',      emoji: '🎪' },
  salon:       { label: 'Salon / Spa / Beauty',       emoji: '✂️' },
  medical:     { label: 'Medical / Dental',           emoji: '🏥' },
  contractor:  { label: 'Contractor / Home Services', emoji: '🔨' },
  automotive:  { label: 'Automotive',                 emoji: '🚗' },
  other:       { label: 'Other',                      emoji: '📍' },
};

// ─── Status Config ─────────────────────────────────────────────────────────────

export const LOCAL_STATUS_CONFIG = {
  pending:       { label: 'Prospect',       color: 'text-neutral-400',  bg: 'bg-neutral-400/10',  border: 'border-neutral-400/30'  },
  sent:          { label: 'Pitched',        color: 'text-blue-400',     bg: 'bg-blue-400/10',     border: 'border-blue-400/30'     },
  replied:       { label: 'Replied',        color: 'text-purple-400',   bg: 'bg-purple-400/10',   border: 'border-purple-400/30'   },
  booked:        { label: 'Monthly Client', color: 'text-emerald-400',  bg: 'bg-emerald-400/10',  border: 'border-emerald-400/30'  },
  declined:      { label: 'Declined',       color: 'text-red-400',      bg: 'bg-red-400/10',      border: 'border-red-400/30'      },
  noResponse:    { label: 'No Response',    color: 'text-neutral-600',  bg: 'bg-neutral-600/10',  border: 'border-neutral-600/30'  },
};

// ─── Monthly Pricing Tiers ─────────────────────────────────────────────────────

export const PRICING_TIERS = [
  {
    name: 'Starter',
    price: '$449',
    period: '/month',
    description: 'Perfect for small local businesses just getting started with video.',
    deliverables: [
      '1 cinematic brand video (30s)',
      '4 short-form vertical clips',
      'Color grading in DaVinci Resolve',
      '1 on-location shoot per month',
    ],
    highlight: false,
  },
  {
    name: 'Growth',
    price: '$799',
    period: '/month',
    description: 'The most popular package — consistent, high-quality content every month.',
    deliverables: [
      '1 cinematic hero video (60s)',
      '8 short-form vertical clips',
      '48-hour social content turnaround',
      '2 on-location shoots per month',
      'Full DaVinci Resolve color grade + music',
    ],
    highlight: true,
  },
  {
    name: 'Full Partner',
    price: '$1,299',
    period: '/month',
    description: 'Dedicated monthly content operation — you focus on the business, I handle the visuals.',
    deliverables: [
      '2 cinematic hero videos (60–90s each)',
      '15+ short-form vertical clips',
      'Unlimited shoots within the month',
      'Event + product launch coverage',
      'Content strategy + caption writing',
      'Priority 24hr turnaround',
    ],
    highlight: false,
  },
];

// ─── Opportunity Score ─────────────────────────────────────────────────────────
// Returns 1–10. Higher = better opportunity (weaker their online presence).

export function opportunityScore(biz) {
  let score = 0;
  if (!biz.website) score += 5;
  else score += Math.max(0, 5 - (biz.webScore ?? 3));
  if (!biz.instagram && !biz.facebook) score += 5;
  else score += Math.max(0, 5 - (biz.socialScore ?? 3));
  return Math.min(10, score);
}

export function scoreLabel(score) {
  if (score >= 8) return { text: 'High Priority', color: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/30'    };
  if (score >= 5) return { text: 'Med Priority',  color: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/30'  };
  return              { text: 'Low Priority',   color: 'text-neutral-400', bg: 'bg-neutral-400/10', border: 'border-neutral-400/30' };
}

// ─── Seed data (examples — replace with real businesses you research) ──────────

export const LOCAL_BUSINESS_SEED = [
  {
    id: 'lb-seed-1',
    name: 'Example: Local Taproom',
    type: 'brewery',
    address: 'Newtown, PA',
    distanceMiles: 9,
    website: null,
    instagram: null,
    facebook: null,
    webScore: null,
    socialScore: null,
    status: 'pending',
    contactName: '',
    contactEmail: '',
    notes: 'Example entry — replace with a real brewery you find on Google Maps near Yardley.',
    lastContactDate: null,
  },
  {
    id: 'lb-seed-2',
    name: 'Example: Bucks County Boutique',
    type: 'retail',
    address: 'Doylestown, PA',
    distanceMiles: 14,
    website: 'http://example-boutique.com',
    instagram: '@example_boutique',
    facebook: null,
    webScore: 2,
    socialScore: 2,
    status: 'pending',
    contactName: '',
    contactEmail: '',
    notes: 'Example entry — website looks like a 2012 template, IG posts are blurry product photos.',
    lastContactDate: null,
  },
];

// ─── Gap-Aware Email Generator ─────────────────────────────────────────────────
// gaps = result from analyzeWebsite Cloud Function (or null for general pitch)

const TYPE_CONTEXT = {
  restaurant:  'restaurant',
  brewery:     'brewery and taproom',
  retail:      'retail shop',
  fitness:     'fitness studio',
  realestate:  'real estate agency',
  venue:       'event venue',
  salon:       'salon and spa',
  medical:     'medical practice',
  contractor:  'contracting business',
  automotive:  'automotive business',
  other:       'business',
};

const TYPE_PITCH_BASE = {
  restaurant:  `For a restaurant, video is the most powerful tool for driving foot traffic. A cinematic 30-second clip of your signature dish being plated — or the energy in the dining room on a Friday night — can drive more reservations than a month of static posts.`,
  brewery:     `For a craft brewery, video is your biggest untapped marketing asset. A cinematic reel of a fresh pour, a taproom tour, or a limited release drop creates content that makes people stop scrolling and start planning a visit.`,
  retail:      `For a boutique, video transforms passive browsers into buyers who feel like they already know your store. A cinematic walkthrough of new arrivals or a styled product showcase gives your social feed the editorial quality that drives foot traffic.`,
  fitness:     `For a fitness studio, video is the single most effective tool for turning followers into members. A class showcase or a trainer spotlight gives people the "feel" of your space before they ever walk in — and that's what converts.`,
  realestate:  `For a real estate agency, professional video isn't a nice-to-have anymore — it's what separates listings that sell in days from listings that sit. I shoot full property walkthrough tours in 6K on the Blackmagic Cinema Pro — the same resolution used in luxury real estate and commercial photography. A cinematic, color-graded walkthrough makes staged properties look like a magazine shoot, gives buyers the confidence to schedule a showing before they've set foot inside, and helps you attract higher-quality seller leads who expect their listing to look exceptional.`,
  venue:       `For an event venue, your best marketing is showing the transformation — what the space looks like when it's alive. A cinematic highlight reel from your next event is the most powerful sales tool you can have for future bookings.`,
  salon:       `For a salon or spa, video gives potential clients confidence before they ever book. A transformation video or a cinematic tour of your space builds the trust that turns a Google search into a booked appointment.`,
  medical:     `For a medical or dental practice, professional video builds the trust that gets hesitant new patients through the door. A warm practice tour and provider introduction answers the unspoken questions before they ever ask.`,
  contractor:  `For a contractor, before-and-after video is the most powerful proof you can put online. A cinematic timelapse or a completed project walkthrough builds the credibility that converts a Google search into a signed quote.`,
  automotive:  `For an auto business, video is the fastest way to build credibility and show off your craft. A custom build reveal or a service bay tour creates shareable content that keeps your name in front of local buyers.`,
  other:       `Professional video content is the most effective way to differentiate your business in a crowded local market. A single 30-second cinematic clip can drive more engagement than months of static posts.`,
};

export function generateLocalPitchEmail(biz, gaps = null) {
  const name = biz.name;
  const type = biz.type || 'other';
  const _typeContext = TYPE_CONTEXT[type] || 'business'; // eslint-disable-line no-unused-vars
  const hasWebsite = !!biz.website;
  const hasSocial = !!(biz.instagram || biz.facebook);
  const contactFirst = biz.contactName ? biz.contactName.split(' ')[0] : null;
  const greeting = contactFirst ? `Hi ${contactFirst},` : `Hi there,`;

  // ── Build gap-specific offerings ──────────────────────────────────────────
  const gapOfferings = [];
  const gapObservations = [];

  if (gaps && gaps.fetchSuccess) {
    // Video gaps
    if (!gaps.hasAnyVideo) {
      gapObservations.push('no video content on your website');
      gapOfferings.push('  • Cinematic brand video (30–60s) — your signature piece for the homepage, Google Business, and social media');
    }
    if (!gaps.hasYouTubeLink && !gaps.hasYouTubeEmbed) {
      gapObservations.push('no YouTube presence');
      gapOfferings.push('  • YouTube-formatted long-form video — builds organic discovery and gives your brand a professional home base on video');
    }

    // Social gaps
    if (!gaps.hasInstagramLink && !biz.instagram) {
      gapObservations.push('no Instagram linked');
      gapOfferings.push('  • Instagram Reels content — vertical, algorithm-optimized clips that grow reach organically');
    }
    if (!gaps.hasTikTokLink) {
      gapObservations.push('no TikTok presence');
      gapOfferings.push('  • TikTok short-form clips — the highest organic reach of any platform right now for local businesses');
    }
    if (!gaps.hasFacebookLink && !biz.facebook) {
      gapOfferings.push('  • Facebook video content — still the dominant platform for 30–55 year old local customers');
    }

    // Site quality gaps
    if (gaps.isOutdated && gaps.copyrightYear) {
      gapObservations.push(`website design that appears to be from ${gaps.copyrightYear}`);
      gapOfferings.push(`  • A fresh video presence that modernizes how ${name} looks online without a full website rebuild`);
    }
    if (!gaps.hasMobileViewport) {
      gapObservations.push('a site that may not be mobile-optimized');
      gapOfferings.push('  • Mobile-first vertical video content that looks perfect on every phone and platform');
    }
  } else {
    // No gaps analysis — use manual data
    if (!hasWebsite) {
      gapObservations.push('no website yet');
      gapOfferings.push('  • Cinematic brand video — a professional first impression that works harder than a basic website');
    }
    if (!hasSocial) {
      gapObservations.push('no active social media presence');
      gapOfferings.push('  • Instagram Reels + TikTok short-form clips — start building an audience from zero');
    }
    if (hasWebsite && biz.webScore && biz.webScore <= 2) {
      gapObservations.push('a website that could use a stronger visual presence');
      gapOfferings.push('  • Cinematic brand video for the homepage — the fastest way to upgrade how your site looks and converts');
    }
  }

  // Always include the core monthly offering
  if (!gapOfferings.some(o => o.includes('brand video'))) {
    gapOfferings.unshift('  • Monthly cinematic brand video (30–60s) — your signature content piece, refreshed each month');
  }
  gapOfferings.push('  • 4–8 short-form vertical clips per month — optimized for Reels, TikTok, and Facebook');
  gapOfferings.push('  • 48-hour turnaround on social content — stay relevant while the moment is still live');
  gapOfferings.push('  • Full editing and DaVinci Resolve color grading — every frame looks cinematic and intentional');

  // ── Build the observation opening ─────────────────────────────────────────
  let openingLine;
  if (gapObservations.length > 0) {
    const obs = gapObservations.slice(0, 3);
    if (obs.length === 1) {
      openingLine = `I was looking into ${name} online and noticed ${obs[0]} — which means there's a real opportunity to grow your presence with professional video content.`;
    } else if (obs.length === 2) {
      openingLine = `I was looking into ${name} and noticed ${obs[0]} and ${obs[1]} — which means you're leaving significant discovery and conversion on the table.`;
    } else {
      openingLine = `I spent some time looking at ${name}'s online presence and noticed a few things: ${obs.slice(0, -1).join(', ')}, and ${obs[obs.length - 1]}. Each one is a real opportunity.`;
    }
  } else if (!hasWebsite && !hasSocial) {
    openingLine = `I was searching for ${name} online and noticed you don't have a website or social media presence yet — which means a lot of local customers who would love what you offer simply can't find you.`;
  } else {
    openingLine = `I came across ${name} and noticed there's a real opportunity to level up your online presence with professional video that matches the quality of what you're actually delivering.`;
  }

  const typeParagraph = TYPE_PITCH_BASE[type] || TYPE_PITCH_BASE.other;

  const subject = `Monthly Video Content for ${name} — Local Filmmaker in Yardley, PA`;

  const body = `${greeting}

My name is Mike, and I'm a filmmaker and video producer based in Yardley, PA — right in your neighborhood.

${openingLine}

${typeParagraph}

A bit about my work: I'm the cinematographer behind the video content for Kevin Smith's The Secret Stash and Smodcastle Cinemas — two of New Jersey's most recognized entertainment brands. I shoot on the Blackmagic Cinema 6K Pro with Sigma Art prime lenses — the same cinema-grade camera used on professional film and TV productions — and edit everything in DaVinci Resolve for a polished, cinematic finish.

WHAT I'M OFFERING ${name.toUpperCase()}
Based on what I saw, here's specifically what I'd build for you each month:

${gapOfferings.join('\n')}

HOW IT WORKS
I work with local businesses on a monthly retainer — so you're consistently showing up online with professional video, not just posting when you happen to think of it. Monthly packages start at $449/month for solo businesses and scale based on shoot frequency and deliverable volume. I intentionally keep my local client roster small so each business gets dedicated attention.

I'd love to stop by ${name}, spend 20 minutes seeing what you're working with, and put together a quick sample clip on the first visit so you can see exactly what this would look like for your brand. No pressure, no commitment — just a conversation.

Are you free for a quick call or a brief visit this week?

Mike
Black Magic Films
Yardley, PA
mike@blackmagicfilms.com
https://blackmagicfilms.com
Instagram: @blackmagicfilms_
YouTube: @blackmagickuper`;

  return { subject, body };
}
